import React from 'react';

import { StateChip } from '../../ActionStateChip';
import ClampedText from '../../ClampedText';
import { REQUEST_STATE_APPEARANCE, REQUEST_STATE_CHIP } from '../../constants';
import RejectionReason from '../../RejectionReason';
import RequestGestures from '../../RequestGestures';
import {
  activityCardElementId,
  askRejectionReason,
  askStateLabel,
  logEntryElementId,
  requestOutcome,
} from '../../utils';
import ActivityCard from '../ActivityCard';
import type { RequestRow } from '../types';

interface RequestCardProps {
  row: RequestRow;
  /** Refetches the asks after a gesture, which both panes' request cards resolve from. */
  onAskUpdate: () => Promise<void>;
  /** Brings the request's log entry into view and marks it selected. */
  onSelectLogEntry: (row: RequestRow) => void;
  /** Rings the card: the log last took the reader here. */
  isSelected: boolean;
  onDeselect: () => void;
}

/**
 * A request Clive raised, as a pointer into the log, where Clive's reasoning is read — and, while it
 * waits on the handler, the place to reject it.
 *
 * A recommendation's geometry: the state under the title in every state, Reject bottom left while the
 * request is pending, and the jump to the log bottom right. Once closed it ends on the line the log's
 * card ends on, or on the note it was rejected with.
 */
const RequestCard: React.FC<RequestCardProps> = ({ row, onAskUpdate, onSelectLogEntry, isSelected, onDeselect }) => {
  const { key, ask, state, logEventId } = row;
  const outcome = requestOutcome(state, ask);

  return (
    <ActivityCard
      kind="request"
      appearance={REQUEST_STATE_APPEARANCE[state]}
      title={ask.ask}
      subtitle={ask.summary}
      status={<StateChip label={askStateLabel(state, ask)} className={REQUEST_STATE_CHIP[state]} />}
      createdAt={ask.created_at}
      footer={<RequestGestures request={ask} state={state} onAskUpdate={onAskUpdate} />}
      onSelect={() => onSelectLogEntry(row)}
      controls={logEventId ? logEntryElementId(logEventId) : undefined}
      elementId={activityCardElementId(key)}
      isSelected={isSelected}
      onDeselect={onDeselect}
    >
      <ClampedText>{ask.description}</ClampedText>
      {outcome ? <ClampedText>{outcome}</ClampedText> : null}
      <RejectionReason reason={askRejectionReason(state, ask)} />
    </ActivityCard>
  );
};

export default RequestCard;
